// Lead Contacts tab — name/role/email/phone per row, card grid, inline edit.
//
// Two flavors of contact:
//   - `isSeeded` (primary, lead-create): cannot be deleted. Star icon shown
//     "filled" + non-toggleable. Edit allowed. Backend belt-and-braces.
//   - everything else: editable, deletable, can be promoted to primary.
//
// UI choices vs the previous version:
//   - Header row (avatar + name + role) is its own flex row so the action
//     icons sit cleanly to the right and never overlap the email/phone.
//   - Action icons reveal on row hover (Linear/Notion style) — quieter card
//     at rest.
//   - Email + phone wrap to their own lines, with overflow-wrap so long
//     addresses don't blow out the card width.

import React, { useEffect, useState } from "react";
import {
  Plus, Trash2, Star, Loader2, Mail, Phone as PhoneIcon, Pencil, X, Check,
} from "lucide-react";
import toast from "react-hot-toast";
import { Tooltip } from "antd";
import { Button, IconButton, Input, Badge } from "../../atoms";
import { useLeadContacts, useCreateContact, useUpdateContact, useDeleteContact } from "../../hooks/useLeads";
import useFocusRow, { FOCUS_ROW_STYLE } from "../../hooks/useFocusRow";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";

const EMPTY = { name: "", role: "", email: "", phone: "" };

const PHONE_RE = /^[0-9+\-().\s]*$/;
const isValidPhone = (v) => !v || PHONE_RE.test(String(v));
const stripAlpha = (v) => v.replace(/[a-zA-Z]/g, "");

function initialsFrom(name, email) {
  const source = name || email || "?";
  if (source.includes("@") && !name) return source.slice(0, 2).toUpperCase();
  return source.split(/\s+/).filter(Boolean).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
}

function formatErr(err) {
  return err?.response?.data?.error || err?.message || "Save failed";
}

// ────────────────────────────────────────────────────────────────────────────
// Inline editor — replaces the static card when the user hits the pencil.
// ────────────────────────────────────────────────────────────────────────────
function ContactEditor({ leadId, contact, onDone, onCancel }) {
  const [form, setForm] = useState({
    name: contact.name || "",
    role: contact.role || "",
    email: contact.email || "",
    phone: contact.phone || "",
  });
  const [error, setError] = useState(null);
  const updateContact = useUpdateContact(leadId);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhone = (e) => setForm((f) => ({ ...f, phone: stripAlpha(e.target.value) }));
  const phoneError = form.phone && !isValidPhone(form.phone) ? "Only digits and + - ( ) allowed" : null;

  const submit = async () => {
    setError(null);
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (phoneError) { setError(phoneError); return; }
    try {
      await updateContact.mutateAsync({
        id: contact._id,
        patch: {
          name: form.name.trim(),
          role: form.role.trim() || "",
          email: form.email.trim() || "",
          phone: form.phone.trim() || "",
        },
      });
      onDone?.();
    } catch (err) {
      setError(formatErr(err));
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Input value={form.name} onChange={set("name")} placeholder="Name *" autoFocus />
      <Input value={form.role} onChange={set("role")} placeholder="Role (optional)" />
      <Input value={form.email} onChange={set("email")} type="email" placeholder="Email" />
      <div>
        <Input value={form.phone} onChange={setPhone} placeholder="Phone" inputMode="tel" />
        {phoneError && <div style={{ fontSize: 11, color: "var(--crm-danger)", marginTop: 3 }}>{phoneError}</div>}
      </div>
      {error && <div style={{ fontSize: 12, color: "var(--crm-danger)" }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={updateContact.isPending}>Cancel</Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={updateContact.isPending || !form.name.trim() || !!phoneError}
          leftIcon={updateContact.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Display card — read-only view of a contact with hover-revealed actions.
// ────────────────────────────────────────────────────────────────────────────
function ContactCard({ leadId, contact, onEdit }) {
  const updateContact = useUpdateContact(leadId);
  const deleteContact = useDeleteContact(leadId);
  const [hovered, setHovered] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isSeeded = !!contact.isSeeded;
  const isPrimary = !!contact.isPrimary;
  // Seeded primary stays primary and stays put. Everything else is fully
  // mutable.
  const canDelete = !isSeeded;
  const canPromote = !isPrimary;

  const handlePromote = () => {
    if (!canPromote) return;
    updateContact.mutate(
      { id: contact._id, patch: { isPrimary: true } },
      {
        onSuccess: () => toast.success(`${contact.name} is now primary`),
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };

  const handleDelete = () => {
    if (!canDelete) return;
    setConfirmOpen(true);
  };

  const performDelete = () => {
    deleteContact.mutate(contact._id, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast.success(`${contact.name} removed`);
      },
      onError: (err) => toast.error(formatErr(err)),
    });
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        border: isPrimary ? "1px solid var(--crm-primary)" : "1px solid var(--color-border-subtle)",
        borderRadius: 8,
        padding: 14,
        background: "var(--color-bg-surface)",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        boxShadow: hovered ? "0 1px 3px rgba(0,0,0,0.05)" : "none",
      }}
    >
      {/* Header row: avatar + name/role + actions (right-aligned) */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9999, flexShrink: 0,
          background: isPrimary ? "var(--crm-primary)" : "var(--crm-primary-soft)",
          color: isPrimary ? "#fff" : "var(--crm-primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 600,
        }}>
          {initialsFrom(contact.name, contact.email)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 600,
            color: "var(--color-text-primary)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {contact.name || contact.email || "Untitled"}
          </div>
          {contact.role && (
            <div style={{
              fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {contact.role}
            </div>
          )}
          {isPrimary && (
            <div style={{ marginTop: 6 }}>
              <Badge tone="primary">
                {isSeeded ? "Primary · Locked" : "Primary"}
              </Badge>
            </div>
          )}
        </div>

        {/* Action cluster — sits to the right of the header row, never
            overlaps the name. Reveals on hover; on touch devices the icons
            are always visible (opacity transitions only). */}
        <div style={{
          display: "flex", gap: 2,
          opacity: hovered ? 1 : 0.55,
          transition: "opacity 120ms",
          flexShrink: 0,
        }}>
          {canPromote && (
            <Tooltip title="Make primary" mouseEnterDelay={0.2}>
              <IconButton
                size="sm"
                aria-label="Make primary"
                onClick={handlePromote}
                disabled={updateContact.isPending}
              >
                <Star size={13} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit" mouseEnterDelay={0.2}>
            <IconButton
              size="sm"
              aria-label="Edit contact"
              onClick={() => onEdit(contact._id)}
            >
              <Pencil size={13} />
            </IconButton>
          </Tooltip>
          {canDelete ? (
            <Tooltip title="Remove" mouseEnterDelay={0.2}>
              <IconButton
                size="sm"
                aria-label="Remove contact"
                onClick={handleDelete}
                disabled={deleteContact.isPending}
              >
                <Trash2 size={13} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Primary contact can't be removed" mouseEnterDelay={0.2}>
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 26, height: 26, borderRadius: 6,
                color: "var(--color-text-secondary)", opacity: 0.35, cursor: "not-allowed",
              }}>
                <Trash2 size={13} />
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Body: email + phone, each on its own line with overflow-wrap so
          long values don't expand the card past its grid track. */}
      {(contact.email || contact.phone) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              style={{
                fontSize: 12, color: "var(--crm-primary)", textDecoration: "none",
                display: "inline-flex", alignItems: "center", gap: 6,
                overflowWrap: "anywhere", wordBreak: "break-word",
                lineHeight: 1.4,
              }}
            >
              <Mail size={11} style={{ flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>{contact.email}</span>
            </a>
          )}
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              style={{
                fontSize: 12, color: "var(--color-text-secondary)", textDecoration: "none",
                display: "inline-flex", alignItems: "center", gap: 6,
                lineHeight: 1.4,
              }}
            >
              <PhoneIcon size={11} style={{ flexShrink: 0 }} />
              {contact.phone}
            </a>
          )}
        </div>
      )}
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={performDelete}
        title="Remove contact?"
        message={`${contact.name} will be removed from this lead.`}
        confirmLabel="Remove contact"
        isPending={deleteContact.isPending}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab root.
// ────────────────────────────────────────────────────────────────────────────
export default function ContactsPanel({ leadId, focusItemId, onFocusResolved }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const { data, isLoading } = useLeadContacts(leadId);
  const createContact = useCreateContact(leadId);
  const { register } = useFocusRow(focusItemId, { onResolved: onFocusResolved });
  const contacts = data?.contacts || [];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhone = (e) => setForm((f) => ({ ...f, phone: stripAlpha(e.target.value) }));
  const phoneError = form.phone && !isValidPhone(form.phone) ? "Only digits and + - ( ) allowed" : null;

  // Close any open editor if the lead is re-selected.
  useEffect(() => { setEditingId(null); }, [leadId]);

  const submitNew = async () => {
    setError(null);
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (phoneError) { setError(phoneError); return; }
    try {
      await createContact.mutateAsync({
        name: form.name.trim(),
        role: form.role.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        // Only the very first contact starts as primary. The auto-seeded
        // one from lead-create normally fills this role already.
        isPrimary: contacts.length === 0,
      });
      setForm(EMPTY);
      setAdding(false);
    } catch (err) {
      setError(formatErr(err));
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <style>{FOCUS_ROW_STYLE}</style>
      {/* Header bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          {isLoading ? "Loading…" : (
            contacts.length === 0
              ? "Add the first decision-maker"
              : `${contacts.length} ${contacts.length === 1 ? "contact" : "contacts"}`
          )}
        </div>
        {!adding && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={13} />} onClick={() => setAdding(true)}>
            Add contact
          </Button>
        )}
      </div>

      {/* Inline composer (add new) */}
      {adding && (
        <div style={{ border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 14, background: "var(--color-bg-surface)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <Input value={form.name} onChange={set("name")} placeholder="Name *" autoFocus />
            <Input value={form.role} onChange={set("role")} placeholder="Role (optional)" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={form.email} onChange={set("email")} type="email" placeholder="Email" />
            <div>
              <Input value={form.phone} onChange={setPhone} placeholder="Phone" inputMode="tel" />
              {phoneError && <div style={{ fontSize: 11, color: "var(--crm-danger)", marginTop: 3 }}>{phoneError}</div>}
            </div>
          </div>
          {error && <div style={{ fontSize: 12, color: "var(--crm-danger)", marginTop: 10 }}>{error}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <Button variant="secondary" size="sm" onClick={() => { setAdding(false); setForm(EMPTY); setError(null); }}>Cancel</Button>
            <Button
              size="sm"
              onClick={submitNew}
              disabled={createContact.isPending || !form.name.trim() || !!phoneError}
              leftIcon={createContact.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
            >
              Save contact
            </Button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && contacts.length === 0 && !adding && (
        <div style={{
          fontSize: 13, color: "var(--color-text-secondary)",
          textAlign: "center", padding: "20px 0", fontStyle: "italic",
        }}>
          No contacts yet
        </div>
      )}

      {/* List — card grid. minmax min raised to 300 so cards don't get
          claustrophobic; gap raised to 12 for a calmer rhythm. */}
      {contacts.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {contacts.map((c) => {
            const isEditing = editingId === c._id;
            return (
              <div
                ref={register(c._id)}
                key={c._id}
                style={{
                  border: c.isPrimary ? "1px solid var(--crm-primary)" : "1px solid var(--color-border-subtle)",
                  borderRadius: 8,
                  padding: isEditing ? 14 : 0,
                  background: "var(--color-bg-surface)",
                }}
              >
                {isEditing ? (
                  <ContactEditor
                    leadId={leadId}
                    contact={c}
                    onDone={() => setEditingId(null)}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <ContactCard
                    leadId={leadId}
                    contact={c}
                    onEdit={setEditingId}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
